import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../../components/bulk-selection/index.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { supportApi } from "../client.js";
import { SupportErrorState } from "../components/SupportEmptyState.js";
import type { SupportSlaPolicy } from "../types.js";

/**
 * SLA policies — first-response and resolution targets per priority.
 * Each policy says "for these priority IDs, agents have N minutes to
 * respond and M minutes to resolve". A policy with priority_ids = null
 * is the default fallback.
 *
 * Mirrors the row + dialog pattern used by the Workflows panel: the
 * header action opens <NewSlaDialog>, deletes go through
 * <ConfirmDialog>, and `useBulkSelection` + `Promise.allSettled` over
 * the per-row admin DELETE drives bulk-remove (no `bulk_destroy`
 * endpoint exists yet, so the client fans the calls out itself).
 */
export function SettingsSlaPanel(): JSX.Element {
  const qc = useQueryClient();
  const policiesQuery = useQuery({
    queryKey: ["support", "settings", "sla"],
    queryFn: supportApi.listSlaPolicies,
  });
  const create = useMutation({
    mutationFn: (b: Omit<SupportSlaPolicy, "id">) => supportApi.createSlaPolicy(b),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "settings", "sla"] }),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: Partial<SupportSlaPolicy>;
    }) => supportApi.updateSlaPolicy(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "settings", "sla"] }),
  });
  const remove = useMutation({
    mutationFn: (id: number) => supportApi.deleteSlaPolicy(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "settings", "sla"] }),
  });
  const bulkRemove = useMutation({
    // Fans out to the per-row admin DELETE in parallel. Settled-style
    // so a single 4xx doesn't strand the others mid-flight — the
    // surrounding invalidate refetches whatever actually stuck.
    mutationFn: async (ids: number[]) => {
      await Promise.allSettled(ids.map((id) => supportApi.deleteSlaPolicy(id)));
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "settings", "sla"] }),
  });

  const rows = policiesQuery.data?.data ?? [];
  const bulk = useBulkSelection(rows);

  const [showCreate, setShowCreate] = useState(false);
  // Pending SLA delete (drives single <ConfirmDialog>).
  const [pendingDelete, setPendingDelete] = useState<{ id: number; name: string } | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  return (
    <Box component="section" sx={{ display: "grid", gap: 1.5 }}>
      <Box component="header" sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
            SLA policies
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            First-response and resolution targets. Breaches feed the
            Reports page and trigger escalations.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setShowCreate(true)}
          data-testid="settings-add-sla"
        >
          Add policy
        </Button>
      </Box>

      <BulkActionBar count={bulk.count} onClear={bulk.clear}>
        <BulkButton
          variant="destructive"
          disabled={bulkRemove.isPending}
          onClick={() => setConfirmBulk(true)}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          Delete {bulk.count} {bulk.count === 1 ? "policy" : "policies"}
        </BulkButton>
      </BulkActionBar>

      {policiesQuery.isLoading && <PageLoader />}
      {policiesQuery.isError && (
        <SupportErrorState
          error={policiesQuery.error}
          resourceLabel="SLA policies"
        />
      )}

      {policiesQuery.data && (
        <Box component="ul" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
          {rows.length > 0 && (
            <Box
              component="li"
              sx={{ display: "grid", gridTemplateColumns: SLA_GRID, alignItems: "center", gap: 1.5, px: 1, fontSize: 12, color: "text.disabled" }}
            >
              <BulkCheckbox
                checked={bulk.allOnPageChecked}
                indeterminate={bulk.someOnPageChecked}
                onChange={(checked) => bulk.toggleAllOnPage(checked)}
                ariaLabel="Select all SLA policies"
              />
              <Box component="span" sx={{ gridColumn: "span 6" }}>Select all on page</Box>
            </Box>
          )}
          {rows.map((p) => (
            <SlaRow
              key={p.id}
              policy={p}
              selected={bulk.isSelected(p.id)}
              onToggle={(checked, opts) => bulk.setChecked(p.id, checked, opts)}
              onSave={(body) => update.mutate({ id: p.id, body })}
              onDelete={() => setPendingDelete({ id: p.id, name: p.name })}
              isPending={update.isPending}
            />
          ))}
          {rows.length === 0 && (
            <Box component="li" sx={{ fontSize: 14, color: "text.disabled" }}>
              No SLA policies yet — use “Add policy” above to create one.
            </Box>
          )}
        </Box>
      )}

      <NewSlaDialog
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={(b) =>
          create.mutate(b, { onSuccess: () => setShowCreate(false) })
        }
        isSubmitting={create.isPending}
      />

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete SLA policy?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" will be deleted permanently. Tickets currently bound to this policy will fall back to the default.`
            : ""
        }
        confirmLabel="Delete policy"
        onConfirm={() => {
          if (!pendingDelete) return;
          remove.mutate(pendingDelete.id);
        }}
      />

      <ConfirmDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Delete ${bulk.count} ${bulk.count === 1 ? "policy" : "policies"}?`}
        description="Tickets currently bound to any of these policies will fall back to the default. This cannot be undone."
        confirmLabel={`Delete ${bulk.count} ${bulk.count === 1 ? "policy" : "policies"}`}
        onConfirm={async () => {
          await bulkRemove.mutateAsync([...bulk.selected]);
          bulk.clear();
        }}
      />
    </Box>
  );
}

/**
 * SLA columns — wider than PRIORITY_GRID because the two minute inputs
 * and the active toggle all need fixed slots:
 *
 *   checkbox │ name (1fr) │ first reply │ resolution │ active │ save │ delete
 */
const SLA_GRID = "1rem 1fr 7rem 7rem 5rem 4rem 2rem";

function SlaRow({
  policy,
  selected,
  onToggle,
  onSave,
  onDelete,
  isPending,
}: {
  policy: SupportSlaPolicy;
  selected: boolean;
  onToggle: (checked: boolean, opts: { shiftKey: boolean }) => void;
  onSave: (body: Partial<SupportSlaPolicy>) => void;
  onDelete: () => void;
  isPending: boolean;
}): JSX.Element {
  const [name, setName] = useState(policy.name);
  const [first, setFirst] = useState(policy.first_response_minutes);
  const [res, setRes] = useState(policy.resolution_minutes);
  const [active, setActive] = useState(policy.active);
  const dirty =
    name !== policy.name ||
    first !== policy.first_response_minutes ||
    res !== policy.resolution_minutes ||
    active !== policy.active;
  return (
    <Box
      component="li"
      sx={[
        { display: "grid", gridTemplateColumns: SLA_GRID, alignItems: "center", gap: 1.5, borderRadius: 1, border: 1, p: 1, transition: "background-color 150ms" },
        selected
          ? { borderColor: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.1) }
          : { borderColor: "divider", bgcolor: "surfaceSoft" },
      ]}
    >
      <BulkCheckbox
        checked={selected}
        onChange={onToggle}
        ariaLabel={`Select SLA policy ${policy.name}`}
      />
      <TextField
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ minWidth: 0 }}
      />
      <NumberMinutes value={first} onChange={setFirst} label="First reply" />
      <NumberMinutes value={res} onChange={setRes} label="Resolution" />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
        }
        label={<Box component="span" sx={{ fontSize: 12 }}>Active</Box>}
        sx={{ m: 0 }}
      />
      <Button
        variant="contained"
        size="small"
        disabled={!dirty || isPending}
        onClick={() =>
          onSave({
            name,
            first_response_minutes: first,
            resolution_minutes: res,
            active,
          })
        }
        sx={{ minWidth: 0, px: 1.5, py: 0.5, fontSize: 12 }}
      >
        Save
      </Button>
      <IconButton
        size="small"
        onClick={onDelete}
        title="Delete SLA policy"
        aria-label={`Delete SLA policy ${policy.name}`}
        sx={{
          color: "text.disabled",
          "&:hover": { bgcolor: (t) => alpha(t.palette.error.main, 0.12), color: "error.main" },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

function NumberMinutes({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
}): JSX.Element {
  // Label is sr-only inside the row so the column stays narrow; the
  // dialog uses its own labelled controls instead.
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12, color: "text.disabled" }}>
      <TextField
        type="number"
        value={value}
        inputProps={{ min: 1, "aria-label": label }}
        title={label}
        onChange={(e) => onChange(Number(e.target.value))}
        sx={{ width: 64 }}
      />
      m
    </Box>
  );
}

function NewSlaDialog({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (b: Omit<SupportSlaPolicy, "id">) => void;
  isSubmitting: boolean;
}): JSX.Element {
  const [name, setName] = useState("");
  const [first, setFirst] = useState(60);
  const [res, setRes] = useState(480);

  // Reset whenever the dialog closes so re-opening gives a clean slate
  // instead of leaking the previous draft.
  const handleClose = () => {
    setName("");
    setFirst(60);
    setRes(480);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      first_response_minutes: first,
      resolution_minutes: res,
      priority_ids: null,
      active: true,
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>New SLA policy</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          First-response and resolution targets, in minutes. The new
          policy starts active and applies to every priority until
          you wire it to a specific one.
        </DialogContentText>

        <Box component="form" id="new-sla-form" onSubmit={handleSubmit} sx={{ mt: 2, display: "grid", gap: 1.5 }}>
          <TextField
            autoFocus
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Standard"
            fullWidth
          />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
            <TextField
              type="number"
              label="First reply (min)"
              value={first}
              inputProps={{ min: 1 }}
              onChange={(e) => setFirst(Number(e.target.value))}
              fullWidth
            />
            <TextField
              type="number"
              label="Resolution (min)"
              value={res}
              inputProps={{ min: 1 }}
              onChange={(e) => setRes(Number(e.target.value))}
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          form="new-sla-form"
          disabled={isSubmitting || !name.trim()}
        >
          Add policy
        </Button>
      </DialogActions>
    </Dialog>
  );
}
